import { useCallback } from 'react';

import {
  addTab,
  removeTab,
  renameTab,
  reorderTabs,
  setActiveTab,
  setDefaultTab,
} from './workspaceTabUtils';

/**
 * The tab strip's handlers. Each is workspaceTabUtils plus persistence; the
 * only one with a rule of its own is delete, which snapshots the layout it is
 * about to destroy so the undo can put it back whole.
 */
export default function useWorkspaceTabs({ layoutConfig, updateLayoutAndPersist, offerUndo }) {
  const handleSelectTab = useCallback((tabId) => {
    updateLayoutAndPersist(setActiveTab(layoutConfig, tabId));
  }, [layoutConfig, updateLayoutAndPersist]);

  const handleAddTab = useCallback(() => {
    const withNewTab = addTab(layoutConfig, 'New Tab');
    const added = withNewTab.tabs[withNewTab.tabs.length - 1];
    updateLayoutAndPersist({ ...withNewTab, activeTabId: added.id });
  }, [layoutConfig, updateLayoutAndPersist]);

  const handleRenameTab = useCallback((tabId, newName) => {
    updateLayoutAndPersist(renameTab(layoutConfig, tabId, newName));
  }, [layoutConfig, updateLayoutAndPersist]);

  const handleDeleteTab = useCallback((tabId) => {
    const before = layoutConfig;
    const deleted = (layoutConfig.tabs || []).find((t) => t.id === tabId);
    updateLayoutAndPersist(removeTab(layoutConfig, tabId));
    offerUndo({
      message: `Deleted "${deleted?.name || 'tab'}"`,
      onUndo: () => updateLayoutAndPersist(before),
    });
  }, [layoutConfig, updateLayoutAndPersist, offerUndo]);

  const handleReorderTabs = useCallback((fromIndex, toIndex) => {
    updateLayoutAndPersist(reorderTabs(layoutConfig, fromIndex, toIndex));
  }, [layoutConfig, updateLayoutAndPersist]);

  const handleSetDefaultTab = useCallback((tabId) => {
    updateLayoutAndPersist(setDefaultTab(layoutConfig, tabId));
  }, [layoutConfig, updateLayoutAndPersist]);

  return {
    handleSelectTab,
    handleAddTab,
    handleRenameTab,
    handleDeleteTab,
    handleReorderTabs,
    handleSetDefaultTab,
  };
}
